import React from 'react';
import { Button } from '@/components/ui/button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Facebook, Github, Globe, Instagram, Linkedin, Mail, MessageCircle, Youtube } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import emailjs from 'emailjs-com';

import { CALENDLY_URL, CONTACT_EMAIL } from '@/lib/site';

const Contact = () => {
  const { toast } = useToast();
  const formRef = React.useRef<HTMLFormElement>(null);
  const whatsappUrl = 'https://wa.me/12026998621';

  const EMAILJS_USER_ID = 'IiRfsZbuWCZHwgBlt';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;
    const form = formRef.current;
    try {
      await emailjs.sendForm(
        'service_db1w009',
        'template_0dqc2ot',
        form,
        EMAILJS_USER_ID
      );
      toast({
        title: 'Message sent!',
        description: "Thank you for contacting us. We'll respond shortly.",
      });
      form.reset();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'There was a problem sending your message. Please try again later.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Omnicraft Services | Book a Free 30-Minute Call</title>
        <meta name="description" content="Contact Omnicraft Services for business inquiries, support, or to start your next project. Reach out via form, email, WhatsApp, or schedule a call." />
        <link rel="icon" type="image/png" href="/logo.png" />
        <link rel="apple-touch-icon" href="/logo.png" />
      </Helmet>
      <div className="min-h-screen pt-16">
        {/* Hero Section */}
        <section className="section-padding bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/5">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="mb-6">Contact Us</h1>
              <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-8">
                Have questions about our services or ready to start your project?
                Get in touch with our team, and we'll help you find the solutions you need.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-semibold mb-6">Send us a message</h2>
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input id="name" name="name" placeholder="Your name" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" name="email" type="email" placeholder="Your email" required />
                        </div>
                      </div>
                      {/* Hidden input for reply_to (user's email) for EmailJS */}
                      <input type="hidden" name="reply_to" value={formRef.current?.email?.value || ''} />
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Select name="subject">
                          <SelectTrigger id="subject">
                            <SelectValue placeholder="Select a subject" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="creative">Creative Services</SelectItem>
                            <SelectItem value="tech">Technology Services</SelectItem>
                            <SelectItem value="business">Business Services</SelectItem>
                            <SelectItem value="ai-workflows">AI Workflows & Automation</SelectItem>
                            <SelectItem value="architecture">Architectural Drafting &amp; Visualisation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea 
                          id="message" 
                          name="message"
                          placeholder="How can we help you?" 
                          rows={5} 
                          required 
                        />
                      </div>
                      <div className="flex justify-center">
                        <AnimatedButton 
                          variant="primary"
                          type="submit"
                        >
                          Send Message
                        </AnimatedButton>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-semibold mb-6">Contact Information</h2>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Mail className="h-5 w-5 text-primary mr-3 mt-1" />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-slate-600 dark:text-slate-400">{CONTACT_EMAIL}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MessageCircle className="h-5 w-5 text-primary mr-3 mt-1" />
                      <div>
                        <p className="font-medium">WhatsApp</p>
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                        >
                          +1 (202) 699-8621
                        </a>
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          WhatsApp only — messages, not calls
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
                
                {/* Call Scheduling */}
                <div className="bg-primary/10 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Schedule a Call</h3>
                  <p className="mb-4 text-slate-600 dark:text-slate-400">
                    Prefer to talk directly? Schedule a 30-minute consultation call with our team.
                  </p>
                  <Button asChild variant="outline" size="lg" className="w-full">
                    <a 
                      href={CALENDLY_URL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Calendar className="mr-2 h-5 w-5" /> Book a Time Slot
                    </a>
                  </Button>
                </div>
                
                {/* Social Media */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Connect With Us</h3>
                  <div className="flex flex-wrap gap-4">
                    <Button asChild variant="outline" size="icon">
                      <a href="https://www.linkedin.com/company/omnicraftservices/?viewAsMember=true" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <Linkedin className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="icon">
                      <a href="https://www.youtube.com/@OmniCraftServices" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                        <Youtube className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="icon">
                      <a href="https://www.instagram.com/omnicraft_services" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                        <Instagram className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="icon">
                      <a href="https://www.behance.net/OmniCraftServices" aria-label="Behance" target="_blank" rel="noopener noreferrer">
                        <Globe className="h-5 w-5" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Contact;
